import { addGameObject, createTransformSizeAnimation } from './entity';
import { setVisibilityState } from './simulate';
import { Ease, multiplyColorAlpha255, toSize, v2 } from './helpers';
import { ObjectType } from './shared/api_shared_types';
import {
  AnimationType,
  Color,
  Entity,
  VisibilityState,
  GameManager,
  GameObjectController,
  ImageEnt,
  MoveMode,
  RenderItemType,
  RenderShapeStyle,
  Shape,
  TransformSize,
  type V2,
} from './types';
import * as DataController from './controllers/data_controller';
import { COLORS } from './constants-colors';
import logger from './controllers/logger';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageCircle,
  type RenderPackageImage,
  type RenderPackageItem,
  type RenderPackageRect,
} from './render_package';
import { dt } from '.';

export function controller(): GameObjectController {
  return {
    getNew: null,
    sync: null,
    update,
    createPackage,
  };
}

export function createImageEnt(
  manager: GameManager,
  ent: Entity,
  imageId: string,
  props: Partial<ImageEnt> = {},
): ImageEnt {
  let newImage: ImageEnt = {
    ...ent,
    shape: Shape.RECT,

    objectType: ObjectType.IMAGE,
    imageId,
    aspect: 0,
    requestNewImageId: '',

    appearOnLoad: false,

    imageState: '',

    appearStyle: 'fade_and_scale',
    onImageError: 'white',

    maxWidthEm: 0,
    minWidthEm: 0,
    maxHeightEm: 0,

    backgroundColor: COLORS.TRANSPARENT,
    strokeColor: COLORS.TRANSPARENT,
    strokeWeight: 0,
    padding: 0,

    requestUpdateDimensions: false,

    rotationSpeed: 0,

    alpha: 1, //0 = transparant, 1 is fully opaque
    appearAlpha: 1, //used for appear animation

    unloadImageOnDispose: false,

    dispose,

    ...props,
  };

  if (newImage.shape === Shape.CIRCLE) {
    newImage.height = 2 * newImage.radius;
    newImage.width = newImage.height;
  }

  if (newImage.moveMode === MoveMode.NONE) {
    newImage.moveMode = MoveMode.STATIC;
  }
  newImage.influenceKinetic = false;

  newImage.clickable = false;
  newImage.allowDrag = false;

  if (newImage.appearStyle === 'fade' || newImage.appearStyle === 'fade_and_scale') {
    newImage.appearAlpha = 0;
  }

  addGameObject(manager, newImage);

  if (!newImage.appearOnLoad) {
    setVisibilityState(newImage, VisibilityState.APPEARING);
  }

  return newImage;
}

export function getImageDimensions(
  aspect: number,
  maxWidth: number = 0,
  maxHeight: number = 0,
): [width: number, height: number] {
  let width = 0;
  let height = 0;

  if (maxWidth && maxHeight) {
    const maxAspect = maxWidth / maxHeight;
    if (aspect > maxAspect) {
      width = maxWidth;
      height = width / aspect;
    } else {
      height = maxHeight;
      width = height * aspect;
    }
  } else if (maxWidth) {
    width = maxWidth;
    height = width / aspect;
  } else if (maxHeight) {
    height = maxHeight;
    width = height * aspect;
  }

  return [width, height];
}

function update(imageEnt: ImageEnt) {
  const manager = imageEnt.manager;

  let isDirty = false;

  // let { state, progress } = getEntityStateAndProgress(imageEnt);
  let state = imageEnt.visibilityState;
  let progress = imageEnt.stateProgress;

  if (state === VisibilityState.APPEARING) {
    const t = Ease.outBack(progress);

    if (imageEnt.appearStyle === 'fade') {
      imageEnt.appearAlpha = progress;
    } else if (imageEnt.appearStyle === 'fade_and_scale') {
      imageEnt.scale = 0.8 + 0.2 * t;
      imageEnt.appearAlpha = progress;
    } else {
      imageEnt.scale = 1;
      imageEnt.appearAlpha = 1;
    }
    isDirty = true;
  } else if (state === VisibilityState.DISAPPEARING || state === VisibilityState.HIDING) {
    if (imageEnt.appearStyle === 'fade' || imageEnt.appearStyle === 'fade_and_scale') {
      imageEnt.appearAlpha = 1 - progress;
    }
    isDirty = true;
  } else if (imageEnt.entityStateChanged && state === VisibilityState.DEFAULT) {
    imageEnt.appearAlpha = 1;
    imageEnt.scale = 1;
    isDirty = true;
  }

  if (imageEnt.requestNewImageId) {
    imageEnt.aspect = 0;
    imageEnt.imageState = '';
    imageEnt.imageId = imageEnt.requestNewImageId;
    imageEnt.requestNewImageId = '';
    if (imageEnt.appearOnLoad) {
      if (imageEnt.appearStyle !== 'none') {
        imageEnt.appearAlpha = 0;
      }
      setVisibilityState(imageEnt, VisibilityState.HIDDEN);
    }
  }

  let doUpdateDimensions = imageEnt.manager.space.layoutChanged || imageEnt.requestUpdateDimensions;

  if (imageEnt.imageState !== 'error' && imageEnt.imageState !== 'success') {
    const state = DataController.getImageState(imageEnt.imageId);

    if (state !== imageEnt.imageState) {
      imageEnt.imageState = state;

      if (imageEnt.imageState === 'success') {
        imageEnt.aspect = DataController.getImageAspectRatio(imageEnt.imageId);

        if (imageEnt.appearOnLoad) {
          setVisibilityState(imageEnt, VisibilityState.APPEARING);
        }
        doUpdateDimensions = true;
      }
    }
  }

  if (imageEnt.aspect && doUpdateDimensions && (imageEnt.maxWidthEm || imageEnt.maxHeightEm)) {
    imageEnt.requestUpdateDimensions = false;

    updateDimensions(imageEnt);

    isDirty = true;
    imageEnt.layoutChanged = true;
  }

  if (imageEnt.rotationSpeed !== 0) {
    imageEnt.rotation += imageEnt.rotationSpeed * dt;
    isDirty = true;
  }

  return isDirty;
}

function updateDimensions(imageEnt: ImageEnt) {
  const pxPerUnit = imageEnt.disableAutoSize ? 100 : imageEnt.manager.rootUnitSizePx;
  const maxW = imageEnt.maxWidthEm * pxPerUnit;
  const maxH = imageEnt.maxHeightEm * pxPerUnit;
  let [w, h] = getImageDimensions(imageEnt.aspect, maxW, maxH);

  imageEnt.width = w;
  imageEnt.height = h;

  if (imageEnt.shape === Shape.CIRCLE) {
    imageEnt.radius = w / 2;
  }
}

export function animateToImageSize(
  imageEnt: ImageEnt,
  minWidthEm: number,
  maxWidthEm: number,
  maxHeightEm: number,
) {
  const m = imageEnt.manager;
  const maxW = maxWidthEm * m.rootUnitSizePx;
  const maxH = maxHeightEm * m.rootUnitSizePx;
  let [w, h] = getImageDimensions(imageEnt.aspect, maxW, maxH);

  if (minWidthEm) {
    w = Math.max(w, imageEnt.minWidthEm * m.rootUnitSizePx);
  }

  const to: TransformSize = {
    sizePx: toSize(w, h),
    sizeEm: null,
    // sizeScale: toSize(imageEnt.widthScale, imageEnt.heightScale),
  };

  createTransformSizeAnimation(imageEnt.manager, imageEnt, {
    type: AnimationType.TRANSFORM_SIZE,
    end: to,
    duration: 0.2,
    easeFunction: Ease.inOutQuad,
  });
}

export async function createTestGradient(): Promise<Blob> {
  const oc = new OffscreenCanvas(100, 100);
  const ctx = oc.getContext('2d');

  const myImageData = ctx.createImageData(100, 100);
  const data = myImageData.data;
  for (let i = 0; i < data.length; i += 4) {
    data[i] = 255; // red
    data[i + 1] = 0; // green
    data[i + 2] = 133; // blue
    data[i + 3] = 255; // alpha
  }

  // ctx.putImageData(myImageData, 0, 0);

  let gradient = ctx.createLinearGradient(0, 0, 100, 100);
  gradient.addColorStop(0, colorToRGBString(COLORS.WHITE_100));
  gradient.addColorStop(1, colorToRGBString(COLORS.LIGHTGREY2_100));
  ctx.fillStyle = gradient;
  ctx.rect(0, 0, 100, 100);
  ctx.fill();

  const blob = await oc.convertToBlob({ type: 'base64' });

  return blob;
}

function colorToRGBString(fillColor: Color): any {
  if (fillColor.length === 3) {
    return `rgb(${fillColor[0]},${fillColor[1]},${fillColor[2]})`;
  } else {
    return `rgba(${fillColor[0]},${fillColor[1]},${fillColor[2]},${fillColor[3] / 255})`;
  }
}

function createPackage(object: ImageEnt, position: V2, scale: number,rotation: number): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  const alpha = object.alpha * object.appearAlpha;
  const alpha256 = alpha * 256;

  const style: RenderShapeStyle = {
    fillColor: multiplyColorAlpha255(object.backgroundColor, alpha256),
    strokeWeight: object.strokeWeight,
    strokeColor: multiplyColorAlpha255(object.strokeColor, alpha256),
    lineDash: [],
    lineDashOffset: 0,
    shadow: null,
  };

  if (object.shape === Shape.RECT) {
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x,
      positionY: position.y,
      rotation: rotation,
      width: object.width + object.padding,
      height: object.height + object.padding,
      shapeStyle: style,
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };
    rPackage.push(rect);
  } else if (object.shape === Shape.CIRCLE) {
    let circle: RenderPackageCircle = {
      type: RenderItemType.circle,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x,
      positionY: position.y,
      radius: object.radius + object.padding / 2,
      shapeStyle: style,
      shadow: null,
    };
    rPackage.push(circle);
  }

  const aspect = DataController.getImageAspectRatio(object.imageId);

  if (aspect && object.height && object.width) {
    const w = object.width;
    const h = object.height;

    let im: RenderPackageImage = {
      type: RenderItemType.image,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      imageId: object.imageId,
      positionX: position.x,
      positionY: position.y,

      positionLocalX: -w / 2,
      positionLocalY: -h / 2,

      rotation: rotation,

      width: w,
      height: h,
      shape: object.shape,

      radius: object.radius,
      opacity: alpha,

      flipHorizontally: false,

      // partial: false,
      // sourcePos: { x: 0, y: 0 },
      // sourceWidth: 0,
      // sourceHeight: 0,
    };
    rPackage.push(im);
  }

  return rPackage;
}

function dispose(imageEnt: ImageEnt) {
  if (imageEnt.unloadImageOnDispose) {
    DataController.unloadImages([imageEnt.imageId]);
  }
}
